/**
 * The scan the browser is holding on to, and how to let go of it.
 *
 * A scan is kept in `localStorage` so that leaving for "My scans & progress"
 * and coming back does not throw the result away. That is worth having, and it
 * is also a copy of somebody's audit sitting in a browser: the URL, the
 * readiness result, every probe and its full answer text, the footprint, the
 * drafted FAQ.
 *
 * Signing out did not touch any of it. The next person at that machine pressed
 * reload and got the previous person's scan back, and the Fix it checkboxes with
 * it. On a shared agency machine that is the whole point of signing out, undone.
 *
 * This gets more serious with share links: a viewer's browser would otherwise
 * keep a copy of the owner's audit after the link was revoked.
 */

/** The current scan blob: url, result, probes, footprint, drafted FAQ, tab. */
const SCAN_KEY = 'aivs_scan2';
/**
 * Written by a version that no longer exists. Still cleared, because a browser
 * that visited the old build is exactly the browser holding stale data, and a
 * key nothing writes any more is one nobody thinks to remove.
 */
const LEGACY_SCAN_KEY = 'aivs_scan';
/** `aivs_fixit_<url>`, one per site ever scanned in this browser. */
const FIXIT_PREFIX = 'aivs_fixit_';

/**
 * Everything about the scanned sites, gone. Preferences stay.
 *
 * `aivs_read_about` survives on purpose: it records that this browser has opened
 * the explainer once, which is about the reader, not about anybody's site.
 */
export function clearScanState(): void {
  try {
    localStorage.removeItem(SCAN_KEY);
    localStorage.removeItem(LEGACY_SCAN_KEY);
    // Collect first, then remove: removing during the walk shifts the indexes
    // and silently skips every second key.
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(FIXIT_PREFIX)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    // Private mode, or storage disabled. Nothing was persisted either, so
    // nothing is left behind.
  }
}

/**
 * Sign out so that nothing of the previous person is left on screen.
 *
 * Clearing storage is only half of it. The result also lives in React state in
 * App, in the panels below it and in whatever was fetched into the history view,
 * so a sign-out that only dropped the cookie left the whole audit visible until
 * someone reloaded. Rather than chase that state through a dozen components and
 * miss one, the page is reloaded onto a clean URL: no hash, no query, nothing
 * mounted.
 */
export function hardSignOut(): void {
  clearScanState();
  window.location.replace(window.location.pathname);
}
